
import { supabase } from '@/integrations/supabase/client';

export interface AvatarProfile {
  id: string;
  name: string;
  avatar_url: string | null;
}

// Get uploaded avatar icons from Supabase storage
const getUploadedAvatarIcons = async (): Promise<string[]> => {
  try {
    const categories = ['male', 'female', 'objects', 'general'];
    const allIcons: string[] = [];
    
    for (const category of categories) {
      const folderPath = `avatar-icons/${category}`;
      const { data: files, error } = await supabase
        .storage
        .from('app-assets')
        .list(folderPath, {
          limit: 100,
          sortBy: { column: 'name', order: 'asc' }
        });
      
      if (error) {
        console.warn(`Error fetching icons from ${category}:`, error);
        continue;
      }
      
      if (files && files.length > 0) {
        const iconUrls = files.map(file => {
          const { data: urlData } = supabase
            .storage
            .from('app-assets')
            .getPublicUrl(`${folderPath}/${file.name}`);
          return urlData.publicUrl;
        });
        
        allIcons.push(...iconUrls);
      }
    }
    
    return allIcons;
  } catch (error) {
    console.error('Error fetching uploaded avatar icons:', error);
    return [];
  }
};

// Get a random book avatar from the database
export const getRandomBookAvatar = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('book_avatars')
      .select('image_url')
      .order('display_order');
    
    if (error) throw error;
    if (!data || data.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * data.length);
    return data[randomIndex].image_url;
  } catch (error) {
    console.error('Error fetching random book avatar:', error);
    return null;
  }
};

export const ensureProfilesHaveAvatars = async (profiles: AvatarProfile[], userId?: string): Promise<AvatarProfile[]> => {
  if (!profiles || profiles.length === 0) return profiles;

  const updatedProfiles = await Promise.all(
    profiles.map(async (profile) => {
      // If no avatar, generate one from book avatars
      if (!profile.avatar_url) {
        const newUrl = await getRandomBookAvatar();
        
        if (newUrl && userId) {
          try {
            await supabase
              .from('user_profiles')
              .update({ avatar_url: newUrl })
              .eq('id', profile.id)
              .eq('user_id', userId);
            
            console.log(`Generated book avatar for profile ${profile.name}: ${newUrl}`);
          } catch (error) {
            console.error('Error updating avatar in database:', error);
          }
        }
        
        return { ...profile, avatar_url: newUrl };
      }

      return profile;
    })
  );

  return updatedProfiles;
};

export const generateAvatarForProfile = async (profileName: string, profileId: string): Promise<string | null> => {
  return await getRandomBookAvatar();
};

export const getRandomAvatar = async (): Promise<string | null> => {
  return await getRandomBookAvatar();
};

// Shared helper to update profile avatar in database
export const updateProfileAvatar = async (
  profileId: string,
  avatarUrl: string,
  userId: string
): Promise<{ success: boolean; error?: any; data?: any }> => {
  try {
    console.log('updateProfileAvatar:', { profileId, userId, avatarUrl });
    
    const { data, error } = await supabase
      .from('user_profiles')
      .update({ 
        avatar_url: avatarUrl, 
        updated_at: new Date().toISOString() 
      })
      .eq('id', profileId)
      .eq('user_id', userId)
      .select()
      .single();

    console.log('updateProfileAvatar result:', { data, error });

    if (error) {
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('updateProfileAvatar exception:', error);
    return { success: false, error };
  }
};
